import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Combobox,
  Loader,
  ScrollArea,
  TextInput,
  useCombobox
} from '@mantine/core';
import CustomSelectItem from './CustomSelectItem';
import { triggerNotification } from '../../helpers/notification';
import AppStack from './AppStack';
import AppText from './AppText';

const SearchInput = ({
  onSelect,
  onSearch,
  label,
  placeholder,
  disabled,
  inputBaseProps,
  ...rest
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const combobox = useCombobox({onDropdownClose: () => combobox.resetSelectedOption()
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm.length > 2) {
      setSearchLoading(true);
      // eslint-disable-next-line no-use-before-define
      onSearch(
        { searchTerm: debouncedSearchTerm },
        (results) => {
          setSearchResults(results);
          setSearchLoading(false);
        },
        (e) => {
          triggerNotification(e);
          setSearchLoading(false);
        }
      );
    }
    else {
      setSearchResults([]);
      setSearchLoading(false);
    }
  }, [debouncedSearchTerm]);

  return (
    <Combobox
      disabled={disabled}
      onOptionSubmit={(v) => {
        onSelect(
          v,
          searchResults.find((r) => r.value === v)
        );
        setSearchTerm('');
        setSearchResults([]);
        combobox.closeDropdown();
      }}
      store={combobox}
      withinPortal
      {...rest}
      style={{ minWidth: 0, ...rest.style }}
    >
      <Combobox.Target>
        <TextInput
          label={label}
          onBlur={() => combobox.closeDropdown()}
          onChange={(e) => {
            setSearchTerm(e.currentTarget.value);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          placeholder={placeholder}
          value={searchTerm}
          {...inputBaseProps}
        />
      </Combobox.Target>

      {debouncedSearchTerm.length > 2 && (
        <Combobox.Dropdown style={{ zIndex: 9999 }}>
          {searchLoading ? (
            <AppStack
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                padding: 8
              }}
            >
              <Loader color="dark" size={32} />
            </AppStack>
          ) : searchResults.length === 0 ? (
            <AppStack style={{ padding: 8 }}>
              <AppText
                style={{
                  fontSize: 14,
                  color: '#666',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                No results found.
              </AppText>
            </AppStack>
          ) : (
            <Combobox.Options>
              <ScrollArea.Autosize mah={250} scrollbarSize={5} type="scroll">
                {searchResults.map((d) => (
                  <Combobox.Option key={d.value} value={d.value}>
                    <CustomSelectItem {...d} />
                  </Combobox.Option>
                ))}
              </ScrollArea.Autosize>
            </Combobox.Options>
          )}
        </Combobox.Dropdown>
      )}
    </Combobox>
  );
};

SearchInput.propTypes = {
  onSearch: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  inputBaseProps: PropTypes.object,
  label: PropTypes.string,
  loading: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.any
};

export default SearchInput;
